import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AuthShell } from '../components/AuthShell.tsx';
import { ErrorNotice, Spinner } from '../components/ui.tsx';

interface UnsubscribeDto {
  customerName: string;
  email: string | null;
}

/**
 * Reached from the footer of notification emails. Public — the session uuid in
 * the link is the only credential, as it is for the widget.
 */
export function UnsubscribePage(): React.JSX.Element {
  const { sessionUuid = '' } = useParams<{ sessionUuid: string }>();
  const [state, setState] = useState<'idle' | 'unsubscribed' | 'resubscribed'>('idle');
  const [busy, setBusy] = useState(false);

  const details = useQuery({
    queryKey: ['unsubscribe', sessionUuid],
    queryFn: async () => {
      const response = await fetch(`/public/unsubscribe/${sessionUuid}`);
      if (!response.ok) throw new Error('Not found');
      return (await response.json()) as UnsubscribeDto;
    },
    retry: false,
  });

  const submit = async (action: 'unsubscribe' | 'resubscribe'): Promise<void> => {
    setBusy(true);

    try {
      await fetch(`/public/${action}/${sessionUuid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel: 'email' }),
      });

      setState(action === 'unsubscribe' ? 'unsubscribed' : 'resubscribed');
    } finally {
      setBusy(false);
    }
  };

  if (details.isLoading) {
    return (
      <AuthShell audience="customer" title="Email preferences">
        <div className="flex justify-center py-4 text-brand-600">
          <Spinner className="h-6 w-6" />
        </div>
      </AuthShell>
    );
  }

  if (details.isError || !details.data) {
    return (
      <AuthShell audience="customer" title="Email preferences">
        <ErrorNotice message="We could not find that conversation." />
      </AuthShell>
    );
  }

  if (state === 'unsubscribed') {
    return (
      <AuthShell audience="customer" title="You're unsubscribed" subtitle="You will not get further emails about this conversation.">
        <button
          type="button"
          className="lg-btn-secondary w-full"
          disabled={busy}
          onClick={() => void submit('resubscribe')}
        >
          Changed your mind? Resubscribe
        </button>
      </AuthShell>
    );
  }

  if (state === 'resubscribed') {
    return (
      <AuthShell audience="customer" title="You're subscribed again" subtitle="We will email you when someone replies." />
    );
  }

  return (
    <AuthShell
      audience="customer"
      title="Email preferences"
      subtitle={details.data.email ?? undefined}
    >
      <p className="mb-5 text-sm text-slate-600 dark:text-slate-300">
        Stop receiving email updates about this conversation? You can still reach us any time by
        replying to an earlier message.
      </p>

      <button
        type="button"
        className="lg-btn-danger w-full"
        disabled={busy}
        onClick={() => void submit('unsubscribe')}
      >
        {busy ? <Spinner /> : null}
        Unsubscribe
      </button>
    </AuthShell>
  );
}
